import Toast from 'components/cosmos/toast';
import { TOAST_TYPE } from 'components/cosmos/toast/constants';
import { Polyglot } from 'components/translate';
import { isObject } from 'helpers/Object';
import { action } from 'mobx';
import { AuthService } from 'modules/auth';
import { UserService } from 'modules/user';

import { ALERT, ERRORS } from './constants';
import LoadingPage from './loadingPage';
import Store from './UiStore';

const DEFAULT_DURATION_VALUE = 1500;
const DEFAULT_HEADER_0FFSET_VALUE = 74;

class Service {
  @action
  toggleSidebar(status = !Store.sidebarActive) {
    Store.sidebarActive = status;
  }

  openToast({ type, message, data = {} }) {
    Toast[type]({
      message: Polyglot.t(`alerts.${message}`, { data })
    });
  }

  setAlertSuccess(message, data = {}) {
    this.openToast({ type: TOAST_TYPE.success, message, data });
  }

  setAlertInfo(message, data = {}) {
    this.openToast({ type: TOAST_TYPE.info, message, data });
  }

  setAlertError(message, data = {}) {
    this.openToast({ type: TOAST_TYPE.danger, message, data });
  }

  setAlertWarning(message, data = {}) {
    this.openToast({ type: TOAST_TYPE.warning, message, data });
  }

  setAlertWithText({ type, message }) {
    Toast[type]({ message });
  }

  @action
  setAlert(config) {
    if (!config.timeout) {
      config.timeout = ALERT.TIMEOUT.DEFAULT;
    }

    Store.alert = Object.assign({ data: {} }, config);
  }

  smoothScroll(
    selector,
    duration = DEFAULT_DURATION_VALUE,
    header0ffset = DEFAULT_HEADER_0FFSET_VALUE
  ) {
    // TODO: this adjustment is temporary to resolve the problem in membership app.
    // we have to adjust it to use element ref instead of jquery.
    const scrollableMembershipClassName = '.bw__content';
    const scrollableElement = $(scrollableMembershipClassName).length
      ? $(scrollableMembershipClassName)
      : $('html, body');

    const scrollTop = $(selector).offset()
      ? $(selector).offset().top - header0ffset
      : 0;

    scrollableElement.animate({ scrollTop }, duration);
  }

  handleInvalidToken = (error, successCallback) => {
    const { data } = error || {};
    const isInvalidToken = data && data.error === 'invalid_token';

    if (!isInvalidToken) {
      return;
    }

    UserService.hasInvalidToken = true;
    AuthService.refreshAccountData(true, successCallback);
  };

  isKnownError(data) {
    return (
      isObject(data) &&
      data.errorCode &&
      Object.keys(ERRORS).includes(data.errorCode)
    );
  }

  handleError(error = {}) {
    const { data } = error.response || error;
    const isAlreadyShowingError = Store.alert && Store.alert.message;

    if (isAlreadyShowingError) {
      return;
    }

    if (this.isKnownError(data)) {
      this.handleKnownError(data);
    } else {
      this.setAlertError('unexpected_error');
    }
  }

  getParameterRequiredField(errorDetail) {
    const fieldsName = {
      purchase_date: Polyglot.t('users_import.manually.date.label')
    };

    return fieldsName[errorDetail]
      ? `<b>${fieldsName[errorDetail].toLowerCase()}</b>`
      : '';
  }

  handleKnownError(data) {
    const errorName = ERRORS[data.errorCode];

    switch (data.errorCode) {
      case '0001':
        const parameterErrors = [];

        Object.keys(data.errorDetail).forEach((field) => {
          const validationsArray = data.errorDetail[field].map(
            (item) => item.key
          );

          validationsArray.forEach((validation) => {
            if (validation === 'size_must_be_between') {
              validation = 'size_must_be_between_v2';
            }

            parameterErrors.push(
              Polyglot.t(`alerts.${validation}`, {
                data: { field: Polyglot.t(`general.${field}`) }
              })
            );
          });
        });

        this.setAlertWithText({
          type: ALERT.TYPE.ERROR,
          message: parameterErrors.join('<br>')
        });

        break;
      case '0007':
        this.setAlertError(errorName, {
          hash: data.errorDetail?.replace?.(':', '') || data.errorDetail
        });
        break;
      case '9003':
        this.setAlertError(errorName, {
          field: this.getParameterRequiredField(data.errorDetail)
        });
        break;
      case '0050':
        this.setAlertError(errorName);
        break;
      default:
        this.setAlertError(errorName);
    }
  }

  openNewTabWithLoading = () => {
    const newTabToAvoidPopupBlocker = window.open('', '_blank');

    if (newTabToAvoidPopupBlocker) {
      newTabToAvoidPopupBlocker.document.body.innerHTML = LoadingPage;
    }

    return newTabToAvoidPopupBlocker;
  };
}

export default new Service();
